import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Users, FileText, Columns3, Search, Sparkles, LogOut, Package, Truck, UserCog, Briefcase, Activity, Camera, Factory, History, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { api, useAuth } from "@/lib/auth";
import { useScreenshotGuard } from "@/hooks/use-screenshot-guard";

const SIDEBAR_COLLAPSE_KEY = "sidebar_collapsed";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers", accessKey: "customers_access" },
  { to: "/suppliers", label: "Suppliers", icon: Factory, testid: "nav-suppliers", accessKey: "suppliers_access" },
  { to: "/leads", label: "Leads", icon: Briefcase, testid: "nav-deals" },
  { to: "/pipeline", label: "Pipeline", icon: Columns3, testid: "nav-pipeline" },
  { to: "/followups", label: "Follow-ups", icon: Sparkles, testid: "nav-followups", badgeKey: "needs_attention" },
  { to: "/quotations", label: "Quotations", icon: FileText, testid: "nav-quotations" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/couriers", label: "Couriers", icon: Truck, testid: "nav-couriers" },
  { to: "/users", label: "Users", icon: UserCog, testid: "nav-users", adminOnly: true },
  { to: "/activity", label: "Recent Activity", icon: History, testid: "nav-activity", adminOnly: true },
  { to: "/login-activity", label: "Login Activity", icon: Activity, testid: "nav-login-activity", adminOnly: true },
  { to: "/screenshot-activity", label: "Screenshot Activity", icon: Camera, testid: "nav-screenshot-activity", adminOnly: true },
];

const titleMap = {
  "/dashboard": ["Dashboard", "Overview of your sales operation"],
  "/customers": ["Customers", "Manage leads, accounts & notes"],
  "/suppliers": ["Suppliers", "Manage vendors & what they deal in"],
  "/leads": ["Leads", "All sales opportunities across customers"],
  "/pipeline": ["Pipeline", "Drag leads across stages"],
  "/followups": ["Follow-ups", "Scheduled nudges across your pipeline"],
  "/quotations": ["Quotations", "quotes"],
  "/products": ["Product Master", "Items you quote and sell"],
  "/couriers": ["Courier Tracking", "DHL, Fedex, Bluedart, Aramex, Airwings"],
  "/users": ["User Management", "Admins & sales people"],
  "/activity": ["Recent Activity", "Full audit trail of every change, by user"],
  "/login-activity": ["Login Activity", "User login / logout tracking"],
  "/screenshot-activity": ["Screenshot Activity", "Detected screenshot attempts, by user"],
};

export default function Layout() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [title, sub] = titleMap[loc.pathname] || ["", ""];
  const [fuStats, setFuStats] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1");

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/followups-stats").then((r) => alive && setFuStats(r.data)).catch(() => {});
    tick();
    const t = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [loc.pathname]);

  const doLogout = () => { logout(); nav("/login", { replace: true }); };
  const initials = (user?.name || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  useScreenshotGuard(!!user);

  return (
    <div className="flex min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col"
        style={{ width: collapsed ? 76 : 248, flexShrink: 0, borderRight: "1px solid var(--border)", background: "var(--bg)", position: "sticky", top: 0, height: "100vh", transition: "width 0.18s ease" }}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 flex items-center gap-3" style={{ borderBottom: "1px solid var(--border)", justifyContent: collapsed ? "center" : "flex-start" }}>
          <div
            style={{
              width: 36, height: 36, borderRadius: 6, flexShrink: 0,
              background: "var(--primary)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontFamily: "Cabinet Grotesk", fontSize: 13, letterSpacing: "-0.02em",
            }}
          >
            ZPM
          </div>
          {!collapsed && (
            <div style={{ minWidth: 0 }}>
              <div className="font-display" style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                Zalani Paper Mart
              </div>
              <div className="overline" style={{ fontSize: 9, marginTop: 2 }}>Sales CRM</div>
            </div>
          )}
        </div>

        <nav className="sidebar-nav flex-1 px-3 py-5 space-y-1" style={{ overflowY: "auto", minHeight: 0 }} data-testid="sidebar-nav">
          {NAV.filter((n) => (!n.adminOnly || isAdmin) && (isAdmin || !n.accessKey || user?.[n.accessKey] !== "hide")).map((n) => {
            const badge = n.badgeKey && fuStats ? fuStats[n.badgeKey] : 0;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                title={collapsed ? n.label : undefined}
                className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
                style={collapsed ? { justifyContent: "center", paddingLeft: 8, paddingRight: 8 } : undefined}
              >
                <n.icon size={17} strokeWidth={1.75} />
                {!collapsed && <span style={{ flex: 1 }}>{n.label}</span>}
                {badge > 0 && (collapsed ? (
                  <span
                    style={{ position: "absolute", top: 6, right: 6, width: 8, height: 8, borderRadius: "50%", background: "var(--primary)" }}
                    data-testid={`badge-${n.testid}`}
                  />
                ) : (
                  <span
                    style={{ background: "var(--primary)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10, minWidth: 18, textAlign: "center" }}
                    data-testid={`badge-${n.testid}`}
                  >
                    {badge}
                  </span>
                ))}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="btn-ghost"
            style={{ width: "100%", justifyContent: collapsed ? "center" : "flex-start" }}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>

        <div className="px-5 py-4" style={{ borderTop: "1px solid var(--border)" }}>
          {!collapsed && <div className="overline" style={{ fontSize: 9, marginBottom: 6 }}>Signed in</div>}
          <div className="flex items-center gap-3" data-testid="sidebar-user" style={{ justifyContent: collapsed ? "center" : "flex-start" }}>
            <div style={{ width: 32, height: 32, borderRadius: 6, flexShrink: 0, background: user?.color || "var(--surface-2)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#fff", fontFamily: "Cabinet Grotesk" }}>
              {initials}
            </div>
            {!collapsed && (
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} data-testid="sidebar-user-name">{user?.name || "—"}</div>
                <div style={{ fontSize: 11, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email}</div>
              </div>
            )}
            {!collapsed && (
              <button onClick={doLogout} className="btn-ghost" title="Logout" style={{ padding: 6 }} data-testid="logout-btn">
                <LogOut size={14} />
              </button>
            )}
          </div>
          {collapsed && (
            <button onClick={doLogout} className="btn-ghost" title="Logout" style={{ padding: 6, width: "100%", marginTop: 8, display: "flex", justifyContent: "center" }} data-testid="logout-btn">
              <LogOut size={14} />
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="px-6 md:px-10 py-5 flex items-center justify-between gap-4"
          style={{ borderBottom: "1px solid var(--border)", background: "var(--bg)", position: "sticky", top: 0, zIndex: 50 }}
          data-testid="page-header"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, margin: 0, lineHeight: 1.15 }} data-testid="page-title">{title}</h1>
            </div>
            <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>{sub}</div>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <div style={{ position: "relative" }}>
              <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)" }} />
              <input
                className="input-base"
                placeholder="Quick search…"
                style={{ paddingLeft: 34, width: 260, height: 38 }}
                data-testid="global-search"
              />
            </div>
          </div>
        </header>

        <main className="flex-1 px-6 md:px-10 py-8 fade-in" style={{ maxWidth: 1400, width: "100%" }} data-testid="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
